import { useState } from "react";
import { ShieldAlert, ShieldCheck, Unlock } from "lucide-react";
import type { CockpitContext } from "../../api/client";
import type { PriceTick } from "@/hooks/useWsConnection";
import type { BreakerEntry } from "../../hooks/useBreakerState";
import type { StreamHealthOverrideState } from "../../hooks/useStreamHealthOverride";
import { cn } from "@/lib/utils";
import Panel from "@/components/common/Panel";
import { parisHour } from "@/lib/cockpit";

const STREAM_HEALTH_CODE = "CIRCUIT_STREAM_HEALTH";

interface GatesRailProps {
  /** Dernier tick (spread + fraîcheur + drapeau delayed). Null avant le premier tick. */
  tick: PriceTick | null;
  /** Gardes d'entrée gelées + fenêtre de session (du cycle actif). Null en veille. */
  ctx: CockpitContext | null;
  /** Mode du bot — sélectionne l'application de session (DEMO=warn / LIVE=block). */
  mode?: string | null;
  breakers: BreakerEntry[];
  /** True ssi le WS trading est ouvert. False ⇒ ligne d'erreur explicite. */
  wsAvailable: boolean;
  /** Dérogation ponctuelle spread (owner 2026-09-02) — bouton sur la ligne CIRCUIT_STREAM_HEALTH.
   *  Optionnel : absent ⇒ pas de bouton (compat des appelants qui ne le câblent pas). */
  streamHealthOverride?: StreamHealthOverrideState;
}

/**
 * LA réponse à « pourquoi le bot n'entre pas ? » — les gardes de quote (spread,
 * fraîcheur), la fenêtre de session Paris et l'état des breakers, en un seul rail
 * compact. Les breakers déclenchés se déploient automatiquement (code + détail) ;
 * sinon une ligne « OK ». Honnête : « — » quand le tick ou le contexte manque.
 */
export default function GatesRail({
  tick,
  ctx,
  mode,
  breakers,
  wsAvailable,
  streamHealthOverride,
}: GatesRailProps) {
  const spread = tick?.spread ?? null;
  const age = tick?.quoteAgeMs ?? null;
  const delayed = tick?.delayed === true;

  const spreadOk = spread == null || ctx == null ? null : spread <= ctx.maxSpreadPoints;
  const ageOk = age == null || ctx == null ? null : age <= ctx.maxQuoteAgeMs;

  const hour = parisHour(Date.now());
  const blocked = ctx?.blockedHoursParisLive?.includes(hour) ?? false;
  const enforcement =
    ctx == null ? null : mode === "LIVE" ? ctx.sessionEnforcementLive : ctx.sessionEnforcementDemo;
  const sessionTone = !blocked ? "ok" : enforcement === "BLOCK" ? "bad" : "warn";
  const sessionText = !blocked ? "ouverte" : enforcement === "BLOCK" ? "bloquée" : "warn seul";

  const tripped = breakers.filter((b) => b.status === "tripped");
  const [overrideConfirming, setOverrideConfirming] = useState(false);
  const overrideActive = streamHealthOverride?.status?.active === true;

  const confirmOverride = async () => {
    try {
      await streamHealthOverride?.activate();
    } finally {
      setOverrideConfirming(false);
    }
  };

  return (
    <Panel
      title="Conditions d'entrée"
      aria-label="Conditions d'entrée"
      testId="gates-rail"
      bodyClassName="flex flex-col gap-2"
    >
      {!wsAvailable && (
        <p className="text-loss text-xs" role="alert" data-testid="gates-ws-error">
          WebSocket trading déconnecté — l'état live est indisponible. Reconnexion…
        </p>
      )}
      <Row
        label="Spread"
        testId="gates-spread"
        tone={spreadOk == null ? "muted" : spreadOk ? "ok" : "bad"}
        value={spread == null ? "—" : `${spread.toFixed(1)} pts`}
        note={ctx == null ? undefined : `max ${ctx.maxSpreadPoints}`}
      />
      <Row
        label="Fraîcheur quote"
        testId="gates-age"
        tone={delayed ? "bad" : ageOk == null ? "muted" : ageOk ? "ok" : "bad"}
        value={age == null ? "—" : `${age} ms`}
        note={delayed ? "DIFFÉRÉ" : ctx == null ? undefined : `max ${ctx.maxQuoteAgeMs}`}
      />
      <Row
        label={`Session · Paris ${String(hour).padStart(2, "0")}h`}
        testId="gates-session"
        tone={ctx == null ? "muted" : sessionTone}
        value={ctx == null ? "—" : sessionText}
        note={enforcement == null ? undefined : `${mode === "LIVE" ? "LIVE" : "DEMO"} : ${enforcement}`}
      />

      {/* ── breakers : OK en une ligne, sinon la liste des déclenchés ── */}
      {wsAvailable && tripped.length === 0 && (
        <p className="text-buy flex items-center gap-1.5 text-xs" data-testid="gates-breakers-ok">
          <ShieldCheck className="size-3.5" aria-hidden /> Breakers : aucun blocage
        </p>
      )}
      {wsAvailable && tripped.length > 0 && (
        <div className="flex flex-col gap-1" data-testid="gates-breakers-tripped">
          <p className="text-sell flex items-center gap-1.5 text-xs font-semibold">
            <ShieldAlert className="size-3.5" aria-hidden /> {tripped.length} breaker
            {tripped.length > 1 ? "s" : ""} déclenché{tripped.length > 1 ? "s" : ""}
          </p>
          <ul className="flex flex-col gap-1">
            {tripped.map((b) => (
              <li
                key={b.code}
                className="border-sell/40 bg-sell-soft rounded-md border px-2 py-1.5"
              >
                <code className="text-foreground font-mono text-xs">{b.code}</code>
                {b.detail && (
                  <div className="text-muted-foreground mt-0.5 font-mono text-[0.7rem]">
                    {b.detail}
                  </div>
                )}
                {/* Dérogation ponctuelle spread (owner 2026-09-02) : n'assouplit QUE ce check —
                    les 4 autres (flux, quote, delayed, age) restent pleinement actifs. Consommée
                    seule à la prochaine ouverture bot réussie. */}
                {b.code === STREAM_HEALTH_CODE && streamHealthOverride && !overrideActive && (
                  <div className="mt-1.5">
                    {overrideConfirming ? (
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          onClick={() => void confirmOverride()}
                          disabled={streamHealthOverride.pending}
                          className="bg-warning flex-1 rounded px-2 py-1 text-[0.7rem] font-bold text-black disabled:opacity-50"
                          data-testid="stream-health-override-confirm"
                        >
                          {streamHealthOverride.pending
                            ? "Activation…"
                            : "Confirmer — assouplit le seuil spread jusqu'à la prochaine ouverture"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setOverrideConfirming(false)}
                          className="border-border text-muted-foreground hover:text-foreground rounded border px-2 py-1 text-[0.7rem]"
                        >
                          Annuler
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setOverrideConfirming(true)}
                        className="text-warning border-warning/45 hover:bg-warning/10 inline-flex items-center gap-1 rounded border px-2 py-1 text-[0.7rem] font-medium"
                        data-testid="stream-health-override-activate"
                      >
                        <Unlock className="size-3" aria-hidden /> Ignorer le spread une fois
                      </button>
                    )}
                  </div>
                )}
                {b.code === STREAM_HEALTH_CODE && streamHealthOverride && overrideActive && (
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    <span className="text-warning text-[0.7rem]" data-testid="stream-health-override-active">
                      Dérogation active — se lève à la prochaine ouverture.
                    </span>
                    <button
                      type="button"
                      onClick={() => void streamHealthOverride.cancel()}
                      disabled={streamHealthOverride.pending}
                      className="border-border text-muted-foreground hover:text-foreground rounded border px-2 py-0.5 text-[0.7rem] disabled:opacity-50"
                      data-testid="stream-health-override-cancel"
                    >
                      Annuler
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
}

function Row({
  label,
  value,
  note,
  tone,
  testId,
}: {
  label: string;
  value: string;
  note?: string;
  tone: "ok" | "bad" | "warn" | "muted";
  testId?: string;
}) {
  const toneClass =
    tone === "ok"
      ? "text-buy"
      : tone === "bad"
        ? "text-sell"
        : tone === "warn"
          ? "text-warning"
          : "text-muted-foreground";
  return (
    <div className="flex items-center justify-between gap-2 text-xs" data-testid={testId}>
      <span className="text-muted-foreground">{label}</span>
      <span className="flex items-center gap-2">
        {note && <span className="text-muted-foreground/70 tabular-nums">{note}</span>}
        <span className={cn("font-mono font-semibold tabular-nums", toneClass)}>{value}</span>
      </span>
    </div>
  );
}
